import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { TApiCovenant, TApiRequiredDocument } from '~/modules/covenants/types.ts';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';
import { clientStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/styles.ts';
import type { TThunkError } from '~/store/covenants/types.ts';
import { CovenantListItem } from './CovenantListItem.tsx';

export const COVENANTS_PER_PAGE = 5;

export interface ICovenantsDialogProps {
  open: boolean;
  onClose: () => void;
  document: TApiRequiredDocument | null;
  covenants: TApiCovenant[];
  covenantsTotalElements: number;
  isLoading: boolean;
  error: TThunkError | null;
  onPageChange: (page: number) => void;
}

export const CovenantsDialog: React.FC<ICovenantsDialogProps> = ({
  open,
  onClose,
  document,
  covenants,
  covenantsTotalElements,
  isLoading,
  error,
  onPageChange,
}) => {
  const [expandedCovenantIndex, setExpandedCovenantIndex] = useState<number | null>(null);
  const [covenantsPage, setCovenantsPage] = useState(0);

  const totalPages = Math.ceil(covenantsTotalElements / COVENANTS_PER_PAGE);

  useEffect(() => {
    if (!open) {
      setExpandedCovenantIndex(null);
      setCovenantsPage(0);
    }
  }, [open]);

  useEffect(() => {
    setExpandedCovenantIndex(null);
    setCovenantsPage(0);
  }, [document?.id]);

  useEffect(() => {
    const maxPage = Math.max(0, totalPages - 1);
    if (covenantsPage > maxPage) {
      setCovenantsPage(maxPage);
      setExpandedCovenantIndex(null);
      onPageChange(maxPage);
    }
  }, [covenantsPage, onPageChange, totalPages]);

  const toggleCovenant = (index: number) => {
    setExpandedCovenantIndex((prev) => (prev === index ? null : index));
  };

  const handlePageChange = (page: number) => {
    setExpandedCovenantIndex(null);
    setCovenantsPage(page);
    onPageChange(page);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{ paper: { sx: clientStyles.covDialogPaper } }}
    >
      <DialogTitle sx={clientStyles.covDialogTitle}>
        <Typography component="span" sx={clientStyles.covDialogTitleText}>
          Перегляд ковенантів
        </Typography>

        <IconButton aria-label="Закрити" onClick={onClose} sx={clientStyles.covDialogCloseButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={clientStyles.covDialogContent}>
        <Box sx={clientStyles.covDialogBody}>
          {!document ? (
            <Typography variant="body2" sx={clientStyles.covDialogMessage}>
              Документ не обрано.
            </Typography>
          ) : isLoading ? (
            <Box sx={clientStyles.covDialogLoader}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Typography variant="body2" sx={clientStyles.covDialogErrorMessage}>
              {error.message}
            </Typography>
          ) : !covenants.length ? (
            <Typography variant="body2" sx={clientStyles.covDialogMessage}>
              Немає прив&apos;язаних ковенантів.
            </Typography>
          ) : (
            <>
              <Box sx={clientStyles.covDialogList}>
                {covenants.map((covenant, index) => (
                  <CovenantListItem
                    key={`${covenant.id}-${covenantsPage * COVENANTS_PER_PAGE + index}`}
                    covenant={covenant}
                    index={index}
                    covenantsPage={covenantsPage}
                    covenantsPerPage={COVENANTS_PER_PAGE}
                    expandedCovenantIndex={expandedCovenantIndex}
                    onToggle={toggleCovenant}
                  />
                ))}
              </Box>

              {totalPages > 1 && (
                <Box sx={clientStyles.covDialogPaginationWrap}>
                  <TablePaginationControls
                    page={covenantsPage}
                    rowsPerPage={COVENANTS_PER_PAGE}
                    totalElements={covenantsTotalElements}
                    isLoading={isLoading}
                    showSummary={false}
                    justifyContent="center"
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={() => {}}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={clientStyles.covDialogActions}>
        <Button onClick={onClose} sx={clientStyles.covDialogCloseAction}>
          Закрити
        </Button>
      </DialogActions>
    </Dialog>
  );
};
